import React from 'react';
import { useLocation, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  CircularProgress,
  Typography,
  Box,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useGetCorrectionDiffQuery, useGetCorrectionsQuery } from '../Service/correctionApi';
import {
  useGetInvoiceByIdQuery,
  useGetNextInvoiceNumberQuery,
  useGetSellerCompanyIdByReservationQuery,
  useRegenerateInvoicePdfMutation,
  useReplacePrepaymentsMutation,
  useUpdateInvoiceCommandMutation,
} from '../Service/extendedInvoicesApi';
import { isCorrectionInvoice } from '../Types/correctionTypes';
import { resolveCorrectionPaymentSettlement } from '../utils/correctionPaymentSettlement';
import { InvoiceScheme, InvoiceStatus, InvoiceType, VatRateType } from '../Types/invoicesTypes';
import CustomCard from '../../Shared/Components/CustomCard/CustomCard';
import { InvoiceStatusLabels } from '../Types/invoicesLabels';
import PaymentSection from '../Components/PaymentSection/PaymentSection';
import {
  InvoiceCashDocumentsSection,
  InvoiceBasicsSection,
  InvoiceContractorSection,
  InvoiceDateDriftWarningDialog,
  InvoiceDocumentSection,
  InvoiceItemsSection,
  InvoicePfronRefundSection,
  InvoicePrepaymentPaymentSection,
  InvoiceCorrectionReasonSection,
  InvoiceSellerSection,
  PrepaymentsList,
} from '../Components';
import { normalizeInvoiceType } from '../utils/invoiceEnumNormalization';
import { hasIssueDateMonthYearDrifted } from '../utils/invoiceNumberMonthYear';
import Status from '../../Shared/Components/Status/Status';
import SaveButtons from '../../Shared/Components/SaveButtons/SaveButtons';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import type { CreateInvoiceForm } from '../Types/createInvoiceFormTypes';
import { createInitialInvoiceForm } from '../utils/createInvoiceInitialState';
import {
  buildInvoicePayload,
  normalizeInvoiceTemplateVariant,
} from '../utils/invoiceHelpers';
import { getFieldLabel } from '../utils/fieldLabels';
import { useCrossBorderVatSuggestion } from '../hooks/useCrossBorderVatSuggestion';
import { createInvoiceSchema } from '../Validation/createInvoice.schema';
import { invoiceLogger } from '../utils/invoiceLogger';
import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';
import { applyApiValidationErrorsToForm } from '../utils/invoiceApiErrorHandling';
import { buildValidationSummary } from '../utils/validationSummary';

const normalizeVatType = (vatType: string | undefined): VatRateType => {
  if (!vatType) {
    return VatRateType.Vat23;
  }

  if (Object.values(VatRateType).includes(vatType as VatRateType)) {
    return vatType as VatRateType;
  }

  const normalized = vatType.charAt(0).toUpperCase() + vatType.slice(1);
  return Object.values(VatRateType).includes(normalized as VatRateType)
    ? (normalized as VatRateType)
    : VatRateType.Vat23;
};

const splitAddress = (
  fullAddress?: string,
  streetNo?: string
): { street: string; rest: string } => {
  if (streetNo) {
    return { street: streetNo, rest: fullAddress || '' };
  }

  if (!fullAddress) {
    return { street: '', rest: '' };
  }

  const parts = fullAddress
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length >= 2) {
    const [street, ...rest] = parts;
    return { street, rest: rest.join(', ') };
  }

  return { street: '', rest: fullAddress };
};

const EditInvoice = () => {
  const location = useLocation();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const parsedInvoiceId = invoiceId ? parseInt(invoiceId) : 0;
  const [updateInvoice, updateStatus] = useUpdateInvoiceCommandMutation();
  const [replacePrepayments] = useReplacePrepaymentsMutation();
  const [regenerateInvoicePdf] = useRegenerateInvoicePdfMutation();
  const [submitErrorMessage, setSubmitErrorMessage] = React.useState<string | null>(null);
  const [submitOutcome, setSubmitOutcome] = React.useState<'idle' | 'success' | 'error'>('idle');
  const [postCreateWarning, setPostCreateWarning] = React.useState<string | null>(null);
  const [pendingSubmitData, setPendingSubmitData] = React.useState<CreateInvoiceForm | null>(null);
  const [isDateDriftDialogOpen, setIsDateDriftDialogOpen] = React.useState(false);
  const dateDriftAcknowledgedRef = React.useRef(false);

  const navigationState = location.state as unknown;
  React.useEffect(() => {
    const stateWithPartialWarning = navigationState as { partialWarning?: unknown } | null;
    const warningFromNavigationState =
      stateWithPartialWarning &&
      typeof stateWithPartialWarning === 'object' &&
      typeof stateWithPartialWarning.partialWarning === 'string'
        ? stateWithPartialWarning.partialWarning
        : null;
    setPostCreateWarning(warningFromNavigationState);
  }, [navigationState]);

  const {
    control,
    setValue,
    reset,
    watch,
    getValues,
    setError,
    clearErrors,
    handleSubmit: rhfHandleSubmit,
    formState: { errors },
  } = useForm<CreateInvoiceForm>({
    defaultValues: createInitialInvoiceForm(),
    resolver: yupResolver(createInvoiceSchema) as any,
    mode: 'onChange',
  });
  const validationSummary = React.useMemo(() => buildValidationSummary(errors), [errors]);

  const vatSuggestion = useCrossBorderVatSuggestion({
    setValue,
    getValues,
    autoApply: true,
  });

  const {
    data: invoice,
    error: invoiceError,
    isLoading: invoiceLoading,
  } = useGetInvoiceByIdQuery(parsedInvoiceId, { skip: !invoiceId });

  const {
    data: corrections,
    error: correctionsError,
    isLoading: correctionsLoading,
  } = useGetCorrectionsQuery(parsedInvoiceId, {
    skip: !invoiceId || !invoice || isCorrectionInvoice(invoice),
  });
  const {
    data: correctionDiff,
    error: correctionDiffError,
    isLoading: correctionDiffLoading,
  } = useGetCorrectionDiffQuery(parsedInvoiceId, {
    skip: !invoiceId || !invoice || !isCorrectionInvoice(invoice),
  });

  const watchType = watch('type');
  // Mirrors the backend rule: a stored reservation is locked, except for a Proforma that stays a Proforma.
  const isReservationLocked =
    !!invoice?.reservationId &&
    !(
      normalizeInvoiceType(invoice.type) === InvoiceType.Proforma &&
      normalizeInvoiceType(watchType) === InvoiceType.Proforma
    );
  const watchScheme = watch('scheme');
  const watchReservationId = watch('reservationId');
  const storedReservationId = invoice?.reservationId;
  // A Proforma may change its reservation, but once the lock applies (e.g. type switched away
  // from Proforma) the stored reservation must be restored, or the save would be rejected.
  React.useEffect(() => {
    if (isReservationLocked && storedReservationId && watchReservationId !== storedReservationId) {
      setValue('reservationId', storedReservationId, { shouldDirty: false });
    }
  }, [isReservationLocked, storedReservationId, watchReservationId, setValue]);
  const watchIssueDate = watch('issueDate');
  const watchCompanyId = watch('companyId');
  const watchUseReservationPaymentImport = watch('useReservationPaymentImport');
  const watchSelectedPrepaymentIds = watch('selectedPrepaymentIds') || [];
  const watchTemplateVariant = normalizeInvoiceTemplateVariant(watch('templateVariant'));
  const watchItems = watch('items');
  const watchRefundParticipantReferralCost = watch('refundParticipantReferralCost');
  const watchRefundGuardianReferralCost = watch('refundGuardianReferralCost');
  const watchRefundPfronSubsidyParticipant = watch('refundPfronSubsidyParticipant');
  const watchRefundPfronSubsidyGuardian = watch('refundPfronSubsidyGuardian');
  const watchRefundOwnContribution = watch('refundOwnContribution');

  const liveItemsTotalGross = React.useMemo(() => {
    if (!Array.isArray(watchItems) || watchItems.length === 0) {
      return invoice?.totalGross ?? 0;
    }

    return watchItems.reduce((sum, item) => {
      const quantity = Number(item?.quantity ?? 0);
      const unitGross = Number(item?.unitGross ?? 0);

      return sum + quantity * unitGross;
    }, 0);
  }, [invoice?.totalGross, watchItems]);

  const hasAnyRefundFieldsFilled = [
    watchRefundParticipantReferralCost,
    watchRefundGuardianReferralCost,
    watchRefundPfronSubsidyParticipant,
    watchRefundPfronSubsidyGuardian,
    watchRefundOwnContribution,
  ].some((value) => value !== undefined && value !== null);

  const shouldShowPfronRefundSection = watchType === InvoiceType.Refund || hasAnyRefundFieldsFilled;

  const { data: sellerCompanyId } = useGetSellerCompanyIdByReservationQuery(watchReservationId!, {
    skip: !watchReservationId,
  });

  const { data: nextNumber, isLoading: isNextLoading } = useGetNextInvoiceNumberQuery(
    {
      type: watchType,
      companyId: watchCompanyId || sellerCompanyId,
      issueDate: watchIssueDate,
    },
    { skip: !watchType || (!watchCompanyId && !sellerCompanyId) }
  );

  React.useEffect(() => {
    if (sellerCompanyId) {
      setValue('companyId', sellerCompanyId, { shouldValidate: true, shouldDirty: true });
    }
  }, [sellerCompanyId, setValue]);

  React.useEffect(() => {
    if (!invoice) {
      return;
    }

    const sellerSplit = splitAddress(invoice.sellerAddress, invoice.sellerStreetNo);
    const buyerSplit = splitAddress(invoice.buyerAddress, invoice.buyerStreetNo);
    const recipientSplit = splitAddress(invoice.recipientAddress);

    const normalizedPaymentMethod =
      typeof invoice.paymentMethod === 'string'
        ? invoice.paymentMethod.charAt(0).toLowerCase() + invoice.paymentMethod.slice(1)
        : invoice.paymentMethod;
    const useDifferentRecipient =
      !!invoice.recipientName || !!invoice.recipientAddress || !!invoice.recipientNip;
    const defaultItemQuantity = invoice.type === InvoiceType.Correction ? 0 : 1;
    const mappedItems = (invoice.items || []).map((item) => ({
      id: item.id,
      name: item.name || '',
      description: item.description || '',
      quantity: item.quantity ?? defaultItemQuantity,
      unitNet: item.unitNet || 0,
      unitGross: item.unitGross || 0,
      vatType: normalizeVatType(item.vatType),
      vatExemptNote: item.vatExemptNote || '',
    }));

    invoiceLogger.debug('[EditInvoice] Prefill invoice items:', {
      invoiceId: invoice.id,
      apiItemsCount: invoice.items?.length || 0,
      mappedItemsCount: mappedItems.length,
      mappedItems,
    });

    const prefilledForm: CreateInvoiceForm = {
      ...createInitialInvoiceForm(),
      reservationId: invoice.reservationId || undefined,
      companyId: invoice.companyId || undefined,
      type: (invoice.type as any) || InvoiceType.Standard,
      scheme: (invoice.scheme as any) || InvoiceScheme.Standard,
      currency: invoice.currency || 'PLN',
      issueDate: invoice.issueDate?.split('T')[0] || '',
      saleDate: invoice.saleDate?.split('T')[0] || '',
      placeOfIssue: invoice.placeOfIssue || '',
      paymentMethod: normalizedPaymentMethod as any,
      useReservationPaymentImport: false,
      bankAccount: invoice.bankAccount || invoice.sellerBankAccount || '',
      paymentDueDate: invoice.paymentDueDate?.split('T')[0] || '',
      status: invoice.status,
      paidAmount: invoice.paidAmount,
      paidAt: invoice.paidAt?.split('T')[0] || '',
      paymentComment: invoice.paymentComment || '',
      sellerName: invoice.sellerName || '',
      sellerStreetNo: sellerSplit.street,
      sellerAddress: sellerSplit.rest,
      sellerNip: invoice.sellerNip || '',
      buyerName: invoice.buyerName || '',
      buyerStreetNo: buyerSplit.street,
      buyerAddress: buyerSplit.rest,
      buyerNip: invoice.buyerNip || '',
      buyerCountryIso: (invoice.buyerCountryIso || 'PL').toUpperCase(),
      isBuyerVatEu: invoice.isBuyerVatEu ?? false,
      buyerIsJst: invoice.buyerIsJst ?? false,
      useDifferentRecipient,
      recipientName: invoice.recipientName || '',
      recipientStreetNo: recipientSplit.street,
      recipientAddress: recipientSplit.rest,
      recipientNip: invoice.recipientNip || '',
      comments: invoice.comments || '',
      notes: invoice.notes || '',
      templateVariant: normalizeInvoiceTemplateVariant((invoice as any).templateVariant),
      refundParticipantReferralCost: invoice.refundParticipantReferralCost,
      refundGuardianReferralCost: invoice.refundGuardianReferralCost,
      refundPfronSubsidyParticipant: invoice.refundPfronSubsidyParticipant,
      refundPfronSubsidyGuardian: invoice.refundPfronSubsidyGuardian,
      refundOwnContribution: invoice.refundOwnContribution,
      selectedPrepaymentIds: (invoice.prepaymentInvoices || []).map((x) => x.id),
      items: mappedItems,
    };

    invoiceLogger.debug('[EditInvoice] Hydrating basics from endpoint:', {
      invoiceId: invoice.id,
      reservationId: prefilledForm.reservationId,
      companyId: prefilledForm.companyId,
      type: prefilledForm.type,
      scheme: prefilledForm.scheme,
      currency: prefilledForm.currency,
      itemsCount: prefilledForm.items.length,
    });

    reset(prefilledForm);
  }, [invoice, reset]);

  React.useEffect(() => {
    invoiceLogger.debug('[EditInvoice] RHF items snapshot changed:', {
      count: watchItems?.length || 0,
      items: watchItems,
    });
  }, [watchItems]);

  const performUpdate = React.useCallback(
    async (formData: CreateInvoiceForm) => {
      if (!invoice) {
        return;
      }

      clearErrors();
      setSubmitErrorMessage(null);
      setSubmitOutcome('idle');
      setPostCreateWarning(null);

      invoiceLogger.debug('[EditInvoice] Submit form items:', {
        invoiceId: invoice.id,
        formItemsCount: formData.items?.length || 0,
        formItems: formData.items,
      });

      const payload = buildInvoicePayload(formData);
      const normalizedStatus =
        typeof payload.status === 'number' ? invoice.status : (payload.status as any);
      const normalizedPaymentMethod =
        typeof payload.paymentMethod === 'number'
          ? invoice.paymentMethod
          : (payload.paymentMethod as any);

      const currentLinkedPrepayments = (invoice.prepaymentInvoices || []).map((x) => x.id);
      const selectedPrepaymentIds = formData.selectedPrepaymentIds || [];
      const prepaymentSetChanged =
        selectedPrepaymentIds.length !== currentLinkedPrepayments.length ||
        selectedPrepaymentIds.some((id) => !currentLinkedPrepayments.includes(id));

      const invoiceBase = { ...invoice };
      delete invoiceBase.prepaymentsSettledAmount;
      delete invoiceBase.importedReservationDirectAmount;
      delete invoiceBase.invoicePaymentsNetAmount;

      const updatedInvoice: any = {
        ...invoiceBase,
        type: payload.type ?? invoice.type,
        scheme: payload.scheme ?? invoice.scheme,
        reservationId: formData.reservationId,
        companyId: payload.companyId ?? invoice.companyId,
        issueDate: payload.issueDate,
        saleDate: payload.saleDate,
        placeOfIssue: payload.placeOfIssue,
        paymentMethod: normalizedPaymentMethod,
        useReservationPaymentImport: formData.reservationId
          ? formData.useReservationPaymentImport
          : undefined,
        paymentDueDate: payload.paymentDueDate,
        bankAccount: formData.bankAccount,
        sellerName: payload.sellerName,
        sellerAddress: payload.sellerAddress,
        sellerNip: payload.sellerNip,
        sellerBankAccount: payload.sellerBankAccount,
        buyerName: payload.buyerName,
        buyerAddress: payload.buyerAddress,
        buyerNip: payload.buyerNip,
        buyerCountryIso: payload.buyerCountryIso,
        isBuyerVatEu: payload.isBuyerVatEu,
        buyerIsJst: payload.buyerIsJst,
        recipientName: payload.recipientName,
        recipientAddress: payload.recipientAddress,
        recipientNip: payload.recipientNip,
        comments: payload.comments,
        status: normalizedStatus ?? invoice.status,
        paidAmount: payload.paidAmount ?? invoice.paidAmount,
        paidAt: payload.paidAt ?? invoice.paidAt,
        paymentComment: payload.paymentComment ?? invoice.paymentComment,
        currency: payload.currency,
        notes: formData.notes,
        templateVariant: payload.templateVariant,
        refundParticipantReferralCost: payload.refundParticipantReferralCost,
        refundGuardianReferralCost: payload.refundGuardianReferralCost,
        refundPfronSubsidyParticipant: payload.refundPfronSubsidyParticipant,
        refundPfronSubsidyGuardian: payload.refundPfronSubsidyGuardian,
        refundOwnContribution: payload.refundOwnContribution,
        items: payload.items.map((item, index) => ({
          id: formData.items[index]?.id,
          name: item.name,
          description: item.description,
          quantity: item.quantity,
          unitNet: item.unitNet,
          unitGross: item.unitGross,
          vatRate: item.vatRate,
          vatType: item.vatType ?? VatRateType.Vat23,
          isVatExempt: item.isVatExempt,
          vatExemptNote: item.vatExemptNote,
        })),
      };

      invoiceLogger.debug('[EditInvoice] Update command items payload:', {
        invoiceId: invoice.id,
        type: updatedInvoice.type,
        scheme: updatedInvoice.scheme,
        updatedItemsCount: updatedInvoice.items?.length || 0,
        updatedItems: updatedInvoice.items,
      });

      const settlesPrepayments =
        updatedInvoice.type === InvoiceType.Final && !!formData.reservationId;

      if (settlesPrepayments && prepaymentSetChanged && selectedPrepaymentIds.length === 0) {
        setError('selectedPrepaymentIds', {
          type: 'manual',
          message: 'Faktura końcowa musi rozliczać co najmniej jedną zaliczkę.',
        });
        return;
      }

      try {
        await updateInvoice({
          id: invoice.id,
          invoice: updatedInvoice,
        }).unwrap();

        let shouldRegeneratePdfAfterPrepaymentChange = false;

        if (settlesPrepayments && prepaymentSetChanged) {
          try {
            await replacePrepayments({
              finalInvoiceId: invoice.id,
              prepaymentIds: selectedPrepaymentIds,
              previousPrepaymentIds: currentLinkedPrepayments,
            }).unwrap();
          } catch (replaceError) {
            // The invoice itself is already saved at this point; only the settlement was rejected.
            setSubmitOutcome('error');
            setSubmitErrorMessage(
              `Faktura została zapisana, ale rozliczenie zaliczek nie zostało zmienione. ${extractApiErrorMessage(
                replaceError,
                'Nie udało się zmienić rozliczenia zaliczek.'
              )} Zaznacz zaliczki ponownie i zapisz.`
            );
            invoiceLogger.error('[EditInvoice] Prepayment replace failed after invoice update', {
              invoiceId: invoice.id,
              replaceError,
            });
            return;
          }
          shouldRegeneratePdfAfterPrepaymentChange = true;
        }

        if (shouldRegeneratePdfAfterPrepaymentChange) {
          try {
            await regenerateInvoicePdf({
              id: invoice.id,
              templateVariant: normalizeInvoiceTemplateVariant(formData.templateVariant),
            }).unwrap();
          } catch (regenerateError: any) {
            const warningMessage = extractApiErrorMessage(
              regenerateError,
              'Zmiany zostały zapisane, ale nie udało się odświeżyć PDF. Spróbuj ponownie z widoku edycji.'
            );
            setPostCreateWarning(warningMessage);

            invoiceLogger.error('[EditInvoice] PDF regeneration failed after prepayment updates', {
              invoiceId: invoice.id,
              regenerateError,
            });
          }
        }

        setSubmitOutcome('success');
      } catch (err: any) {
        applyApiValidationErrorsToForm(err, setError);

        const status = typeof err?.status === 'number' ? err.status : undefined;
        const fallbackMessage =
          status && status >= 500
            ? 'Błąd serwera podczas zapisu zmian faktury. Spróbuj ponownie za chwilę.'
            : 'Nie udało się zapisać edycji faktury. Zweryfikuj dane i spróbuj ponownie.';

        setSubmitOutcome('error');
        setSubmitErrorMessage(extractApiErrorMessage(err, fallbackMessage));
        throw err;
      }
    },
    [clearErrors, invoice, regenerateInvoicePdf, replacePrepayments, setError, updateInvoice]
  );

  const handleFormSubmit = React.useCallback(
    async (formData: CreateInvoiceForm) => {
      if (
        !dateDriftAcknowledgedRef.current &&
        invoice &&
        hasIssueDateMonthYearDrifted(invoice.invoiceNumber, formData.issueDate)
      ) {
        setPendingSubmitData(formData);
        setIsDateDriftDialogOpen(true);
        return;
      }

      dateDriftAcknowledgedRef.current = false;
      await performUpdate(formData);
    },
    [invoice, performUpdate]
  );

  const handleDateDriftDialogCancel = React.useCallback(() => {
    setIsDateDriftDialogOpen(false);
    setPendingSubmitData(null);
  }, []);

  const handleDateDriftDialogConfirm = React.useCallback(() => {
    dateDriftAcknowledgedRef.current = true;
    setIsDateDriftDialogOpen(false);
    if (pendingSubmitData) {
      void performUpdate(pendingSubmitData);
    }
  }, [pendingSubmitData, performUpdate]);

  // Loading state
  if (invoiceLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (invoiceError) {
    return (
      <Alert severity="error">
        <AlertTitle>Błąd</AlertTitle>
        {extractApiErrorMessage(
          invoiceError,
          'Nie udało się pobrać danych faktury. Sprawdź połączenie i spróbuj ponownie.'
        )}
      </Alert>
    );
  }

  // No invoice found
  if (!invoice) {
    return (
      <Alert severity="warning">
        <AlertTitle>Uwaga</AlertTitle>
        Nie znaleziono faktury o podanym identyfikatorze.
      </Alert>
    );
  }

  const isCorrection = isCorrectionInvoice(invoice);
  const correctionPaymentSettlement = resolveCorrectionPaymentSettlement(invoice);

  return (
    <Box>
      {/* Alert with link to base invoice (for corrections) */}
      {isCorrection && invoice.correctionInvoiceId && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            <RouterLink
              to={`/invoices/edit/${invoice.correctionInvoiceId}`}
              style={{ textDecoration: 'none' }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: 'primary.main',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Przejdź do faktury bazowej
              </Typography>
            </RouterLink>
          }
        >
          <AlertTitle>Faktura korygująca</AlertTitle>
          Ta faktura jest korektą do faktury bazowej. Kliknij "Przejdź do faktury bazowej", aby
          zobaczyć oryginalną fakturę.
        </Alert>
      )}

      {isCorrection && (
        <CustomCard title="Porównanie przed / po korekcie" sx={{ mt: 3 }}>
          {correctionDiffLoading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : correctionDiffError ? (
            <Alert severity="warning">
              {extractApiErrorMessage(
                correctionDiffError,
                'Nie udało się pobrać danych porównawczych przed/po korekcie.'
              )}
            </Alert>
          ) : correctionDiff && correctionDiff.items?.length > 0 ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Faktura bazowa: <strong>{correctionDiff.baseInvoiceNumber}</strong>
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Pozycja</TableCell>
                    <TableCell align="right">Ilość przed</TableCell>
                    <TableCell align="right">Ilość po</TableCell>
                    <TableCell align="right">Netto przed</TableCell>
                    <TableCell align="right">Netto po</TableCell>
                    <TableCell align="right">Delta netto</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {correctionDiff.items.map((item, index) => (
                    <TableRow key={`${item.name}-${index}`}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell align="right">{item.quantityBefore}</TableCell>
                      <TableCell align="right">{item.quantityAfter}</TableCell>
                      <TableCell align="right">{item.totalNetBefore.toFixed(2)}</TableCell>
                      <TableCell align="right">{item.totalNetAfter.toFixed(2)}</TableCell>
                      <TableCell align="right">{item.totalNetDelta.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell>
                      <strong>Suma brutto</strong>
                    </TableCell>
                    <TableCell align="right" colSpan={2}>
                      {correctionDiff.totalGrossBefore.toFixed(2)}
                    </TableCell>
                    <TableCell align="right" colSpan={2}>
                      {correctionDiff.totalGrossAfter.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      <strong>{correctionDiff.totalGrossDelta.toFixed(2)}</strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Brak danych porównawczych dla tej korekty.
            </Typography>
          )}
        </CustomCard>
      )}

      {/* Table with list of corrections (for base invoices) */}
      {!isCorrection && (
        <CustomCard title="Faktury korygujące" sx={{ mt: 3 }}>
          {correctionsLoading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : correctionsError ? (
            <Alert severity="error">
              {extractApiErrorMessage(
                correctionsError,
                'Nie udało się pobrać listy faktur korygujących. Spróbuj ponownie później.'
              )}
            </Alert>
          ) : !corrections || corrections.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              Brak faktur korygujących dla tej faktury.
            </Typography>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Numer faktury</TableCell>
                  <TableCell>Data wystawienia</TableCell>
                  <TableCell align="right">Kwota brutto</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Akcje</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {corrections.map((correction) => (
                  <TableRow key={correction.id}>
                    <TableCell>{correction.invoiceNumber}</TableCell>
                    <TableCell>
                      {new Date(correction.issueDate).toLocaleDateString('pl-PL')}
                    </TableCell>
                    <TableCell align="right">
                      {correction.totalGross.toFixed(2)} {correction.currency}
                    </TableCell>
                    <TableCell>{InvoiceStatusLabels[correction.status]}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edytuj">
                        <IconButton
                          component={RouterLink}
                          to={`/invoices/edit/${correction.id}`}
                          size="small"
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CustomCard>
      )}

      <Box sx={{ mt: 3 }}>
        <CustomCard title="Edycja pozycji FV i TP">
          <Box component="form" onSubmit={rhfHandleSubmit(handleFormSubmit)}>
            <InvoiceBasicsSection
              control={control as any}
              errors={errors}
              setValue={setValue}
              nextNumber={nextNumber}
              isNextLoading={isNextLoading}
              sellerCompanyId={sellerCompanyId}
              isReservationLocked={isReservationLocked}
            />

            {watchReservationId && (
              <Box mt={3}>
                <CustomCard title="Import wpłat z rezerwacji">
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Włącz opcję tylko wtedy, gdy chcesz ponownie przeliczyć płatności z aktualnych
                    wpłat rezerwacji.
                  </Alert>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!watchUseReservationPaymentImport}
                        onChange={(_, checked) =>
                          setValue('useReservationPaymentImport', checked, {
                            shouldDirty: true,
                            shouldValidate: false,
                            shouldTouch: false,
                          })
                        }
                      />
                    }
                    label="Przelicz ponownie płatności z rezerwacji przy zapisie"
                  />
                </CustomCard>
              </Box>
            )}

            {watchType === InvoiceType.Prepayment && (
              <InvoicePrepaymentPaymentSection control={control as any} errors={errors} />
            )}

            <InvoiceDocumentSection control={control as any} errors={errors} setValue={setValue} />

            {watchType === InvoiceType.Correction && (
              <InvoiceCorrectionReasonSection control={control as any} errors={errors} />
            )}

            <Box mt={3}>
              <CustomCard title="Szablon PDF">
                <FormControl fullWidth>
                  <InputLabel>Szablon PDF</InputLabel>
                  <Select
                    value={watchTemplateVariant}
                    onChange={(e) =>
                      setValue('templateVariant', e.target.value, {
                        shouldDirty: true,
                        shouldValidate: false,
                      })
                    }
                    label="Szablon PDF"
                  >
                    <MenuItem value="modern">Modern (Premium) - kolorowy</MenuItem>
                  </Select>
                </FormControl>
              </CustomCard>
            </Box>

            <InvoiceSellerSection control={control as any} errors={errors} />

            <InvoiceContractorSection
              control={control as any}
              errors={errors}
              onBuyerNipChange={vatSuggestion.checkAndApply}
            />

            <InvoiceItemsSection
              control={control as any}
              setValue={setValue}
              errors={errors}
              reservationId={watchReservationId}
              scheme={watchScheme}
              invoiceType={watchType}
              disableReservationPrefill={true}
              onItemAdded={vatSuggestion.checkAndApply}
            />

            {watchType === InvoiceType.Final && watchReservationId && (
              <Box mt={3}>
                <CustomCard title="Rozliczenie zaliczek">
                  <PrepaymentsList
                    reservationId={watchReservationId}
                    selectedPrepaymentIds={watchSelectedPrepaymentIds}
                    onSelectionChange={(ids) =>
                      setValue('selectedPrepaymentIds', ids, {
                        shouldDirty: true,
                        shouldValidate: false,
                      })
                    }
                    disabled={updateStatus.isLoading}
                  />
                </CustomCard>
              </Box>
            )}

            {shouldShowPfronRefundSection && (
              <InvoicePfronRefundSection control={control as any} errors={errors} />
            )}

            <Collapse in={validationSummary.length > 0}>
              <Alert
                severity="error"
                icon={<ErrorOutlineIcon />}
                sx={{
                  mt: 2,
                  mb: 2,
                  position: 'sticky',
                  bottom: 80,
                  zIndex: 1000,
                  boxShadow: 3,
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Formularz zawiera błędy walidacji ({validationSummary.length})
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Proszę poprawić poniższe pola przed zapisaniem:
                </Typography>
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  {validationSummary.map(({ field, message }) => (
                    <li key={`${field}-${message ?? 'validation'}`}>
                      <Typography variant="body2">
                        <strong>{getFieldLabel(field)}:</strong> {message || 'Błąd walidacji'}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            </Collapse>

            <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
              <Status
                isLoading={updateStatus.isLoading}
                isError={false}
                isSuccess={submitOutcome === 'success'}
              />
              <SaveButtons
                isUpdating={updateStatus.isLoading}
                handleSubmit={async () => {
                  try {
                    await rhfHandleSubmit(handleFormSubmit)();
                  } catch {
                    // Error state is already surfaced via submitErrorMessage and field-level errors.
                  }
                }}
              />
            </Box>

            {postCreateWarning && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <AlertTitle>Uwaga po zapisie</AlertTitle>
                {postCreateWarning}
              </Alert>
            )}

            {submitErrorMessage && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <AlertTitle>Błąd zapisu</AlertTitle>
                {submitErrorMessage}
              </Alert>
            )}
          </Box>
        </CustomCard>
      </Box>

      {/* Payment Section - only for issued invoices (not drafts) */}
      {invoice && invoice.status !== InvoiceStatus.Draft && (
        <PaymentSection
          invoiceId={invoice.id}
          totalGross={liveItemsTotalGross}
          paidAmount={invoice.paidAmount ?? 0}
          settledOnBaseInvoice={
            correctionPaymentSettlement
              ? {
                  baseInvoiceNumber: correctionDiff?.baseInvoiceNumber,
                  paidAt: correctionPaymentSettlement.paidAt,
                }
              : undefined
          }
        />
      )}

      {/* Cash Documents Section (KP/KW) */}
      {invoice && (
        <Box sx={{ mt: 7, pt: 4 }}>
          <Divider sx={{ mb: 2 }}>
            <Typography variant="overline" color="text.secondary" sx={{ px: 1 }}>
              Ewidencja kasowa
            </Typography>
          </Divider>
          <InvoiceCashDocumentsSection
            invoiceId={invoice.id}
            showCreateButton={!correctionPaymentSettlement}
          />
        </Box>
      )}

      <InvoiceDateDriftWarningDialog
        open={isDateDriftDialogOpen}
        invoiceNumber={invoice.invoiceNumber}
        newIssueDate={pendingSubmitData?.issueDate ?? ''}
        onConfirm={handleDateDriftDialogConfirm}
        onCancel={handleDateDriftDialogCancel}
        isSubmitting={updateStatus.isLoading}
      />
    </Box>
  );
};

export default EditInvoice;
